import { IContact } from "./Contact";
import { IJob } from "./Job";
import { IQuote } from "./Quote";
import { IVehicle } from "./Vehicle";
import { BaseEntity, DefaultResponseWithData } from "./system";

export interface ILeadResponse {
  lastExported: string | null;
  lastImported: string | null;
  leads: ILead[];
}

export interface INewLead {
  CCID: string | null,
  website: LeadSource | null,
  source: string | null,
  price: number | null,
  commission: number | null,
  vehicleGrade: string | null,
  vehicle: {
    vrm: string | null,
    make: string | null,
    model: string | null,
    postcode: string | null,
    line1: string | null,
    line2: string | null,
    line3: string | null,
    line4: string | null,
    locality: string | null,
    town: string | null,
    county: string | null
  },
  contact: {
    fullname: string | null,
    email: string | null
    telephone1: string | null,
    telephone2: string | null,
  }
}

export interface ISingleLeadRespose extends DefaultResponseWithData<ILead> { }

export interface IAllLeadsResponse extends DefaultResponseWithData<ILead[]> { }

export interface IPaymentsLeadsResponse extends DefaultResponseWithData<ILead[]> { }

export interface IUnscheduledLeadsResponse extends DefaultResponseWithData<ILead[]> { }

export enum LeadStatus {
  "Awaiting Contact",
  "Scheduled",
  "Cancelled",
  "Reschedule",
  "Collected",
  "Awaiting Payment",
  "Awaiting COD",
  "Customer to Call Back",
  "Completed"
}

export enum LeadSource {
  GPM,
  SCC,
  CTB,
  RMC,
  Motorwise,
  All,
  SellAVehicle,
  Widget
}

export enum PaymentStatus {
  "Not Started",
  "Raised",
  "Sent",
  "Failed",
  "Approved"
}

export enum LeadQualified {
  "No Reply",
  "Qualified",
  "Stop",
  "No Match"
}

/**
 * Whether this lead went to the customer who won its quote, and if not, why not.
 * The two duplicate outcomes mean a second booking exists for the same vehicle —
 * see `duplicateOfLeadId` for the first one and `routingReason` for the explanation.
 *
 * Numeric values are stored by the API, so the order must not change.
 */
export enum LeadRoutingOutcome {
  Normal = 0,
  DivertedDuplicateVrm = 1,
  FlaggedDuplicateVrm = 2,
  /** Marketplace lead SAV holds for allocation. The only booking for the car - not a duplicate. */
  HeldForAllocation = 3
}

/** Short label for the duplicate badge in a table row. */
export const leadRoutingOutcomeLabel: Record<LeadRoutingOutcome, string> = {
  [LeadRoutingOutcome.Normal]: "",
  [LeadRoutingOutcome.DivertedDuplicateVrm]: "Duplicate · sent to SAV",
  [LeadRoutingOutcome.FlaggedDuplicateVrm]: "Duplicate vehicle",
  [LeadRoutingOutcome.HeldForAllocation]: ""
};

// Only the two duplicate outcomes. HeldForAllocation is also non-Normal but is a single booking,
// and matching it put a false "Second booking" banner on every lead SAV holds for allocation.
export const isDuplicateRouting = (outcome?: LeadRoutingOutcome | null) =>
  outcome === LeadRoutingOutcome.DivertedDuplicateVrm ||
  outcome === LeadRoutingOutcome.FlaggedDuplicateVrm;


/** A correction to the seller's form answers, made from the Quote Details panel. */
export interface IUpdateQuoteDetailsRequest {
  v5C: boolean;
  keys: number;
  accessToVehicle: boolean;
  roadSidePayment: boolean;
  fastPayment: boolean;
  /** yyyy-MM-dd, or null to clear it. */
  preferredCollectionDate: string | null;
}

export interface ILead extends BaseEntity {
  tripId: string | null;
  truckId: string | null;
  contactId: string | null;
  driverId: string | null;
  driver: IContact | null;
  ccid: string | null;
  contact: IContact | null;
  officeNotes: string | null;
  driverNotes: string | null;
  /** Kept on the lead, not the vehicle: the widget's vehicle row is shared by every customer's lead for that reg. */
  vehicleNotes: string | null;
  /** The collection date the seller picked on the quote. A preference only; collectionOn is the booking. */
  preferredCollectionDate: string | null;
  /**
   * The seller's form answers as the yard is told them. They start as the quote's and can be
   * corrected on the lead; the quote keeps what the seller actually said.
   */
  v5C: boolean;
  keys: number;
  accessToVehicle: boolean;
  roadSidePayment: boolean;
  fastPayment: boolean;
  vehicleId: string | null;
  vehicle: IVehicle | null;
  /** The reg the seller typed. Differs from vehicle.vrm (the current plate) when a private plate has moved. */
  vehicleRegistration: string | null;
  /** See LeadRoutingOutcome. Normal on every ordinary lead. */
  routingOutcome: LeadRoutingOutcome;
  /** Why this lead is owned by whoever owns it. Null unless routingOutcome is not Normal. */
  routingReason: string | null;
  /** The booking that already held this vehicle registration. Null unless a duplicate. */
  duplicateOfLeadId: string | null;
  status: LeadStatus;
  website: LeadSource;
  source: string | null;
  price: number;
  commission: number;
  notes: string;
  completedOn: string;
  /** Set when the lead was moved to Cancelled; cleared when it is reopened. */
  cancelledOn?: string | null;
  cancellationReason?: string | null;
  cancellationNote?: string | null;
  qualified: LeadQualified;
  exportedOn: string | null;
  paymentStatus: PaymentStatus;
  paymentMessage: string | null;
  paymentRaisedBy: string | null;
  paymentApprovedBy: string | null;
  paymentConfirmedOn: string;
  paymentType: string | null;
  accountName: string | null;
  accountNumber: string | null;
  sortCode: string | null;
  paymentReference: string | null;
  cardNumber: string | null;
  payeeDOB: string | null;
  chequeName: string | null;
  chequeNumber: string | null;
  premiumPriceUsed: string | null;
  scheme: string | null;
  vehicleGrade: string | null;
  callCount: number;
  callCountUpdatedOn: string | null;
  callCountUpdatedBy: string | null;
  documentsRequested: string | null;
  order: number;
  jobs: IJob[] | null;
  catalyticAveragePrice: number | null;

  quote: IQuote | null;

  customerId: string | null;
}
